import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Container,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { apiList, invokeApi } from "../../services/apiServices";
import { config } from "../../config/config";

export default function GalleryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies();

  const [photo, setPhoto] = useState(null);
  const [relatedPhotos, setRelatedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug everything
  console.log("=== DEBUG GALLERY DETAIL ===");
  console.log("useParams():", useParams());
  console.log("ID from useParams():", id);
  console.log("Full location:", location);
  console.log("Pathname:", location.pathname);
  console.log("URL:", window.location.href);
  console.log("============================");

  // Fetch photo details
  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get ID from multiple sources
        let photoId = id;

        // If id is undefined, try to extract from pathname
        if (!photoId) {
          // Extract ID from pathname - handle different route patterns
          const pathSegments = location.pathname.split("/");
          photoId = pathSegments[pathSegments.length - 1];

          // If the last segment is empty, take the previous one
          if (!photoId || photoId === "view-photo") {
            photoId = pathSegments[pathSegments.length - 2];
          }
        }

        console.log("Final photo ID to fetch:", photoId);

        if (!photoId || photoId === "undefined" || photoId === "null") {
          setError("No photo ID found. Please check the URL.");
          setLoading(false);
          return;
        }

        // Fetch the specific photo
        const response = await invokeApi(
          config.getMyCollege + apiList.getPhoto,
          { id: photoId },
          cookies
        );

        console.log("Photo API Response:", response);

        if (response?.status === 200) {
          // Handle different response structures
          const photoData =
            response.data?.photo || response.data?.data || response.data;

          console.log("Extracted photo data:", photoData);

          if (photoData) {
            const formattedPhoto = {
              id: photoData.id || photoId,
              image:
                photoData.featuredImage ||
                photoData.imageUrl ||
                photoData.image,
              title: photoData.title || `Photo ${photoData.id || photoId}`,
              status: photoData.status || "active",
              createdBy: photoData.createdBy || "Unknown",
              createdDate:
                photoData.createdDate ||
                photoData.created_at ||
                new Date().toISOString(),
              updatedBy: photoData.updatedBy,
              updatedDate: photoData.updatedDate,
            };

            setPhoto(formattedPhoto);

            // Now fetch all photos to get related ones
            await fetchRelatedPhotos(photoId);
          } else {
            setError("Photo data not found in response");
          }
        } else {
          setError(`Failed to fetch photo: ${response?.status}`);
        }
      } catch (error) {
        console.error("Photo API error:", error);
        setError(`Failed to load photo data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const fetchRelatedPhotos = async (currentId) => {
      try {
        const response = await invokeApi(
          config.getMyCollege + apiList.getPhotos,
          null,
          cookies
        );

        console.log("Related photos API response:", response);

        if (response?.status === 200) {
          let photosData = [];

          // Handle different response structures
          if (Array.isArray(response.data)) {
            photosData = response.data;
          } else if (
            response.data?.photos &&
            Array.isArray(response.data.photos)
          ) {
            photosData = response.data.photos;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            photosData = response.data.data;
          } else if (typeof response.data === "object") {
            photosData = Object.values(response.data);
          }

          // Filter active photos and exclude current photo
          const formattedPhotos = photosData
            .filter(
              (p) =>
                p &&
                (p.status === "active" || p.status === "Active") &&
                p.id !== currentId
            )
            .slice(0, 3)
            .map((p) => ({
              id: p.id,
              image: p.featuredImage || p.imageUrl || p.image,
              title: p.title || `Photo ${p.id}`,
              status: p.status,
              createdBy: p.createdBy || "Unknown",
              createdDate: p.createdDate || p.created_at,
            }));

          setRelatedPhotos(formattedPhotos);
        }
      } catch (error) {
        console.error("Related photos API error:", error);
      }
    };

    fetchPhoto();
    window.scrollTo(0, 0);
  }, [id, cookies, location]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRelatedPhotoClick = (relatedId) => {
    navigate(`/photo/${relatedId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !photo) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || "Photo not found"}
        </Alert>
        <Button variant="contained" onClick={handleBack}>
          Go Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate("/photo-manager/photo-list")}
          sx={{ ml: 2 }}
        >
          Go to Photos List
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 2.5 }}>
        {/* Back Button */}
        <Button variant="outlined" onClick={handleBack} sx={{ mb: 2.5 }}>
          ← Back to Gallery
        </Button>

        <Grid container spacing={4}>
          <Grid item xs={12} md={12}>
            {/* Main Photo */}
            <Card
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: 3,
                width: "100%", 
              }}
            >
              <CardMedia
                component="img"
                image={photo.image}
                alt={photo.title}
                sx={{
                  width: "100%", 
                  height: "480px", 
                  objectFit: "cover", 
                  backgroundColor: "#f5f5f5",
                }}
                onError={(e) => {
                  console.error("Image failed to load:", photo.image);
                  e.target.src = "/default-image.jpg";
                }}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Related Photos Section */}
        {relatedPhotos.length > 0 && (
          <Box sx={{ mt: 5 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ mb: 2.5, fontWeight: "bold" }}
            >
              Related Photos
            </Typography>

            <Grid container spacing={3}>
              {relatedPhotos.map((relatedPhoto) => (
                <Grid item xs={12} sm={6} md={4} key={relatedPhoto.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
                      },
                    }}
                    onClick={() => handleRelatedPhotoClick(relatedPhoto.id)}
                  >
                    <CardMedia
                      component="img"
                      height="220"
                      image={relatedPhoto.image}
                      alt={relatedPhoto.title}
                      sx={{ objectFit: "cover" }}
                      onError={(e) => {
                        console.error(
                          "Related image failed to load:",
                          relatedPhoto.image
                        );
                        e.target.src = "/default-image.jpg";
                      }}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom noWrap>
                        {relatedPhoto.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        By {relatedPhoto.createdBy}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* No Related Photos Message */}
        {relatedPhotos.length === 0 && (
          <Box sx={{ mt: 8, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No related photos found
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
